/**
 * PF Revamp — BE group → FE shell mapper.
 * ======================================
 *
 * Folds the DEPLOYED grouped BE catalogue (`ProductCatalogGroupedV2DTO` —
 * `productGroups[]`, each `{ productGroupCode, products[] }`) into the FE's
 * shell-grouped `ProductCatalogueV2` that the transformer + drawer UI expect.
 *
 * HISTORY (2026-07-29): until now the BE returned a FLAT
 * product list and the FE invented the grouping (`groupFlatCatalogue`, buckets
 * keyed `category|kind`). Salesforce-side grouping shipped, so the group is now
 * BE-owned and this file consumes `productGroupCode` instead of guessing. Both
 * consumed mimetypes return the grouped shape ⇒ hard switch, no flat fallback
 * (the flat envelope survives only on `/external-service/…`, which the FE never
 * calls, and its FE type is deleted). The mock fixture is a real grouped capture,
 * so there is exactly ONE shape in the codebase.
 *
 * Real group codes are MERCHANDISING buckets, not product kinds — dev returns
 * `VIRTUAL_STAGING_AND_RENDERING`, `FLOOR_PLANS`, `DOCUMENTATION`, `VIDEO`,
 * `DRONE_PHOTO`, `CONSUMPTION_CERTIFICATE`. That is why names live in i18n
 * keyed by code, and why `variantLabel` falls back to `kind` inside a group.
 *
 * ─── GROUPING RULE: shell = `productGroupCode` × `category` ─────────────────
 * A shell is ONE group within ONE category. Group alone would be wrong: it is
 * `category` — not the group — that drives
 *   (a) the catalogue tabs (`V2_CATEGORY_TO_SLUG`, transform.ts),
 *   (b) meeting gating (`ONSITE_SHOOTING_CATEGORY_IDS = {'on_site_capture'}`,
 *       read by `selectDefaultScheduledByCT` / `selectInstructionsStepSkipped`),
 *   (c) the legacy order category (`deriveOrderCategory`).
 * So if BE ever puts e.g. FOOD, PRODUCT and REAL_ESTATE photography in one
 * `GROUND_PHOTO` group, merging them into a single card would cross-wire all
 * three mechanisms. Splitting a mixed group by category is strictly safer and a
 * no-op for single-category groups.
 *
 * A cross-category split is EXPECTED, not a backend defect: BE has confirmed a
 * WorkType (→ `productGroupCode`) is a merchandising unit, not a type — e.g.
 * `360 Staging` genuinely spans STAGING + EDITING. Every split logs at `info`
 * (localhost/dev console only — see `.claude/rules/typescript.md`), NOT
 * `logger.warn`; do not restore the warn level, it would just re-flood Sentry
 * with an intended, increasingly common shape as BE's 2026-08-12 WorkType sync
 * (40 groups / 567 products) rolls out.
 *
 * ─── NAMES ─────────────────────────────────────────────────────────────────
 * The wire carries NO names. This mapper stays **pure** (no i18n import, unit
 * testable): it emits `productGroupCode` plus a humanized fallback
 * `displayName`, and the i18n overlay happens in the transformer via the
 * resolver injected by `useCatalogue`
 * (`purchase_flow_revamp:product_groups.<GROUP_CODE>.*`). Variant labels are
 * still FE-derived (see `variantLabel`) — the wire has no per-product names.
 *
 * ─── ORDER ─────────────────────────────────────────────────────────────────
 * Shells: the group's own `displayOrder` (Salesforce), then wire
 * order for groups Salesforce has not placed, then group code alpha.
 * Variants: **wire order verbatim** — BE already sorts them gross-price
 * ascending with unpriced last. Do not re-sort.
 */

import type {
  CatalogProductV2DTO,
  CatalogueVariantV2,
  ProductCatalogGroupedV2DTO,
  ProductCatalogueV2,
  ProductCategoryV2,
  ShellProductV2
} from 'models/purchaseFlowRevamp'
import { logger } from 'utils/logging'
import { previewFromThumbnail } from './purchaseFlowRevampCatalogue.thumbnail'

/* -------------------------------------------------------------------------- *
 *  Curation tables  (@bridge-only — the wire carries no presentation data)
 * -------------------------------------------------------------------------- */


/** A group Salesforce has not placed sorts after every placed one, keeping wire
 *  order among themselves. Above any plausible `Catalogue_Display_Order__c`. */
const UNPLACED_ORDER_BASE = 1_000_000

/** Neutral, price-ranked tier vocabulary for un-named multi-variant shells. */
const TIER_LABELS = ['Basic', 'Standard', 'Premium', 'Plus', 'Max', 'Ultra']

/* -------------------------------------------------------------------------- *
 *  Helpers
 * -------------------------------------------------------------------------- */

/**
 * Overlay lookup keys for a shell, most specific first:
 *   1. `<GROUP_CODE>__<CATEGORY>` — addresses ONE split of a multi-category
 *      group (e.g. `GROUND_PHOTO__FOOD` = "Food photography").
 *   2. `<GROUP_CODE>` — the whole group.
 * Used by the transformer's i18n name lookup.
 */
export const groupOverlayKeys = (productGroupCode: string, category: ProductCategoryV2): string[] => [
  `${productGroupCode}__${category}`,
  productGroupCode,
]

/** Synthetic, stable shell id. Opaque — used for UI grouping / cart joins only;
 *  the wire always carries the VARIANT productCode, never this.
 *  Category is appended ONLY when a mixed group had to be split, so
 *  single-category groups keep the shorter `shell_<group>` form. */
const shellCode = (productGroupCode: string, category: ProductCategoryV2, split: boolean): string => {
  const group = String(productGroupCode || 'misc').toLowerCase()
  return split ? `shell_${group}_${String(category).toLowerCase()}` : `shell_${group}`
}

const humanizeKind = (kind: string): string =>
  (kind || 'product')
    .replace(/[-_]+/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())

/** Label a bundle from its member list, e.g. `2× Ground Photo` or
 *  `1× Photo + 1× Floor Plan`. The wire gives member `kind`s but no names. */
const bundleLabel = (members: NonNullable<CatalogProductV2DTO['bundledProducts']>): string => {
  const counts = new Map<string, number>()
  for (const m of members) counts.set(m.kind, (counts.get(m.kind) ?? 0) + 1)
  return [...counts.entries()].map(([kind, n]) => `${n}× ${humanizeKind(kind)}`).join(' + ')
}

/**
 * Variant label (bridge — the wire carries no variant names):
 *   1. bundle → member summary
 *   2. single variant → the shell name
 *   3. shell mixes product KINDS → humanized kind, + a price-tier suffix when
 *      that kind itself has several variants
 *   4. one kind, ≤6 variants → neutral price-tier (BE order is price-ascending)
 *   5. larger → "Option N".
 *
 * Rule 3 exists because real groups are merchandising buckets, not kinds: the
 * dev `VIRTUAL_STAGING_AND_RENDERING`/STAGING shell holds residential AND
 * commercial staging, and `VIDEO` holds ground/drone/ground+drone. Bare tiers
 * ("Basic/Standard/Premium/Plus") would tell the user nothing about WHICH
 * product they are buying. Kind is the only differentiator the wire gives us.
 *
 * ⚠️ These labels are English-only FE bridge strings (as `TIER_LABELS` always
 * were) and are now only a FALLBACK: the transformer prefers a real
 * `product:<productCode>` translation and reaches these only when none exists.
 */
const variantLabel = (
  product: CatalogProductV2DTO,
  index: number,
  shellName: string,
  siblings: CatalogProductV2DTO[]
): string => {
  if (product.bundledProducts?.length) return bundleLabel(product.bundledProducts)
  if (siblings.length === 1) return shellName

  const kinds = new Set(siblings.map((sibling) => sibling.kind))
  if (kinds.size > 1) {
    const sameKind = siblings.filter((sibling) => sibling.kind === product.kind)
    const kindLabel = humanizeKind(product.kind)
    if (sameKind.length === 1) return kindLabel
    const tierIndex = sameKind.indexOf(product)
    const tier = tierIndex < TIER_LABELS.length ? TIER_LABELS[tierIndex] : `Option ${tierIndex + 1}`
    return `${kindLabel} — ${tier}`
  }

  return index < TIER_LABELS.length ? TIER_LABELS[index] : `Option ${index + 1}`
}

/** Map a wire product node (recursively, for `dependents`) onto the FE variant
 *  shape. `displayName` is the FE-derived label; addon and extra labels get
 *  the same `kind`-humanizing enrichment (some wire `kind`s come back blank). */
const toVariant = (product: CatalogProductV2DTO, displayName: string): CatalogueVariantV2 => ({
  productCode: product.productCode,
  category: product.category,
  kind: product.kind,
  area: product.area,
  documentSelectionGroup: product.documentSelectionGroup ?? null,
  price: product.price ?? null,
  // Carried verbatim; the BE only sends one when there is a real saving to strike.
  basePrice: product.basePrice ?? null,
  addons: (product.addons ?? []).map((addon) => ({
    ...addon,
    displayName: addon.displayName ?? (addon.kind ? humanizeKind(addon.kind) : undefined),
  })),
  extras: (product.extras ?? []).map((extra) => ({
    ...extra,
    displayName: extra.displayName ?? (extra.kind ? humanizeKind(extra.kind) : undefined),
  })),
  // Upsell nodes are full products — resolve them with the same mapper so the
  // transformer can carry them through (P4 Option A: carried, not rendered).
  dependents: (product.dependents ?? []).map((dependent) =>
    toVariant(dependent, humanizeKind(dependent.kind))
  ),
  bundledProducts: product.bundledProducts ?? [],
  bundleType: product.bundleType ?? null,
  requiredConfiguration: product.requiredConfiguration ?? null,
  inputImagePolicy: product.inputImagePolicy,
  units: product.units ?? null,
  baseProductCode: product.baseProductCode ?? null,
  displayName,
})

/* -------------------------------------------------------------------------- *
 *  Entry point
 * -------------------------------------------------------------------------- */

/**
 * Map the grouped v2 catalogue response onto FE shells.
 * Pure & deterministic. Assumes the input is already scoped to ONE property's
 * area (`all` + the property's band) — which is what BE sends per session.
 */
export function mapGroupedCatalogue(wire: ProductCatalogGroupedV2DTO): ProductCatalogueV2 {
  // Fail LOUD on an unexpected envelope (e.g. a pre-ISSUE-2231 flat payload, or
  // an error body that slipped through) instead of resolving to a plausible-
  // looking EMPTY catalogue: the query then errors and the catalogue page shows
  // `catalog.load_error`, which is honest, rather than "no products available".
  // An empty `productGroups` array IS legitimate (org with nothing enabled).
  if (!Array.isArray(wire?.productGroups)) {
    logger.error('Unexpected v2 catalogue envelope — expected { productGroups: [...] }', undefined, {
      context: {
        component: 'purchaseFlowRevampCatalogue.grouper',
        action: 'mapGroupedCatalogue',
        metadata: { receivedKeys: Object.keys(wire ?? {}).join(',') },
      },
    })
    throw new Error('Unexpected v2 catalogue response shape: missing productGroups')
  }

  const shells: ShellProductV2[] = []

  wire.productGroups.forEach((group, wireIndex) => {
    const products = group.products ?? []
    if (products.length === 0) return

    // Split a group that spans several categories (see header). Insertion order
    // = first appearance in the wire, so the dominant category stays first.
    const byCategory = new Map<ProductCategoryV2, CatalogProductV2DTO[]>()
    for (const product of products) {
      const bucket = byCategory.get(product.category)
      if (bucket) bucket.push(product)
      else byCategory.set(product.category, [product])
    }

    const split = byCategory.size > 1
    if (split) {
      // `info`, not `warn` — BE has confirmed a WorkType spanning several
      // categories is an intended merchandising unit (e.g. `360 Staging` =
      // STAGING + EDITING), not a defect to chase. `logger.warn` reports to
      // Sentry by default; `info` reaches nowhere in production (console
      // only, gated to LOCALHOST/DEVELOPMENT — see `.claude/rules/typescript.md`),
      // which is exactly right for a localhost-only debug aid on EXPECTED
      // input. Do not restore `warn` here: BE's 40-group WorkType sync makes
      // this fire MORE, not less. The split behaviour itself is unchanged.
      logger.info(
        'Product group spans multiple categories — split into one shell per category (expected — WorkType is a merchandising unit, not a type)',
        {
          productGroupCode: group.productGroupCode,
          categories: [...byCategory.keys()],
        },
        {
          context: {
            component: 'purchaseFlowRevampCatalogue.grouper',
            action: 'mapGroupedCatalogue',
            metadata: {
              productGroupCode: group.productGroupCode,
              categories: [...byCategory.keys()].join(','),
            },
          },
        }
      )
    }

    for (const [category, entries] of byCategory) {
      const kinds = new Set(entries.map((entry) => entry.kind))
      if (kinds.size > 1) {
        logger.warn(
          'Product group shell mixes product kinds — using the first product kind',
          { productGroupCode: group.productGroupCode, category, kinds: [...kinds] },
          {
            context: {
              component: 'purchaseFlowRevampCatalogue.grouper',
              action: 'mapGroupedCatalogue',
              metadata: {
                productGroupCode: group.productGroupCode,
                category,
                kinds: [...kinds].join(','),
              },
            },
          }
        )
      }

      // Fallback name only — the transformer overlays the i18n string keyed by
      // `productGroupCode` (see header).
      const shellName = humanizeKind(group.productGroupCode)
      // Wire order preserved verbatim: BE sorts price-ascending, unpriced last.
      const variants = entries.map((product, index) =>
        toVariant(product, variantLabel(product, index, shellName, entries))
      )


      shells.push({
        productCode: shellCode(group.productGroupCode, category, split),
        category,
        productGroupCode: group.productGroupCode,
        kind: entries[0].kind,
        displayName: shellName,
        displayOrder: group.displayOrder ?? UNPLACED_ORDER_BASE + wireIndex,
        catalogueCategory: group.catalogueCategory ?? undefined,
        preview: previewFromThumbnail(group.thumbnail),
        products: variants,
      })
    }
  })

  // Salesforce order first, then wire order for unplaced groups (both encoded in
  // displayOrder), then group code alpha as a deterministic tiebreak.
  shells.sort((a, b) => {
    const ao = a.displayOrder ?? Number.POSITIVE_INFINITY
    const bo = b.displayOrder ?? Number.POSITIVE_INFINITY
    if (ao !== bo) return ao - bo
    return (a.productGroupCode ?? '').localeCompare(b.productGroupCode ?? '')
  })

  return {
    products: shells,
    bundles: (wire.bundles ?? []).map(toBundleShell),
    featured: wire.featured?.map((product) => product.productCode),
    discountPercentage: wire.discountPercentage ?? null,
    stagingRenovation: wire.stagingRenovation ?? null,
  }
}

/**
 * One bundle → one single-variant shell.
 *
 * A bundle has no `productGroupCode` — that is why it is served outside
 * `productGroups` at all — so the shell carries none, and the transformer falls
 * back to resolving a name from the bundle's own product code. The shell id is
 * synthetic and never reaches the wire; the VARIANT keeps the real product code,
 * which is what an order line sends.
 */
const toBundleShell = (bundle: CatalogProductV2DTO): ShellProductV2 => ({
  productCode: `bundle_${bundle.productCode}`,
  category: bundle.category,
  kind: bundle.kind,
  preview: previewFromThumbnail(bundle.thumbnail),
  products: [toVariant(bundle, humanizeKind(bundle.kind))],
})
